import axios from 'axios';
import * as React from 'react';

import { secondsPerDay } from './consts';

// The washroom screen is glanced at, not used: no controls, nothing to tap,
// and it deliberately loads without a control key so it physically cannot
// drive anything in the house.

interface CountdownEvent {
    targetDate: Date,
    name: string,
    emoji?: string,
}

interface RainWindow {
    startHour: number,
    endHour: number | null,
    chance: number,
}

interface HourlyPoint {
    hour: number,
    tempC: number,
    precip: number,
    code: number,
}

interface WashroomWeather {
    temperatureC: number | null,
    feelsLikeC: number | null,
    highC: number | null,
    lowC: number | null,
    precipChance: number | null,
    weatherCode: number | null,
    rain: RainWindow | null,
    hourly: HourlyPoint[],
    // Only read by the big forecast panel. Optional so a server that predates
    // them (or a failed fetch) just leaves that panel empty.
    hourlyLong?: HourlyPoint[],
    days?: DailyPoint[],
}

// One day of the week strip. `date` is Open-Meteo's bare 'YYYY-MM-DD' in the
// display zone - see dayLabel for why it's never handed to new Date() as-is.
interface DailyPoint {
    date: string,
    highC: number,
    lowC: number,
    precip: number,
    code: number,
}

// What fills the space beside the clock. See /get-washroom-config in index.js.
type WashroomPanel = 'forecast' | 'subway';

interface TrainArrival {
    route: string,
    minutes: number,
}

interface SpineStop {
    name: string,
    key: boolean,
    // Per route (1,2,3): the strand's state through this row, and separately
    // whether the route actually stops here.
    seg: (string | null)[],
    stop: (string | null)[],
}

// One train currently inside the drawn stretch. Deliberately terse keys -
// this is ~20 of them per board every 45s, and the fields are only ever read
// right here. See placeTrain in index.js for how the server derives them.
interface TrainPosition {
    // Index into the board's route list (so ['1','2','3'][r]).
    r: number,
    // Fractional row index: 10.4 means 40% of the way from row 10 to row 11.
    y: number,
    // Direction of travel. 'N' runs toward row 0.
    d: string,
    // Minutes between this train and the reference station, unsigned. The
    // feed's own ETA where it still carries that stop; once NYCT has dropped
    // it (which happens the moment the train is past), the trip's own pace
    // over the rows in between instead. Capped at 99.
    n: number,
}

interface TrainBoard {
    station: string,
    spine: SpineStop[],
    positions: TrainPosition[],
    uptown: TrainArrival[],
    downtown: TrainArrival[],
    alerts: string[],
}

// IRT (1/2/3, from Chambers St), IND (A/C/E, from the unrelated station that
// happens to share the name), IRT Lex (4/5/6, from Bklyn Bridge-City Hall)
// and BMT (N/Q/R/W, from Canal St) - four boards, same shape.
interface TrainBoards {
    irt: TrainBoard | null,
    ind: TrainBoard | null,
    lex: TrainBoard | null,
    bmt: TrainBoard | null,
}

// Mirrors /get-commute in index.js. Everything past `active` is absent when
// there's no train to catch.
interface CommuteData {
    active: boolean,
    event?: { name: string, location: string, start: string },
    route?: {
        route: string, direction: string, station: string,
        headsign: string, walkMinutes: number, totalMinutes: number,
    },
    trainAt?: string,
    leaveInMinutes?: number,
}

interface SunTimes {
    nextRising: string | null,
    nextSetting: string | null,
}

interface WashroomState {
    now: Date,
    events: CountdownEvent[],
    weather: WashroomWeather,
    sun: SunTimes,
    trains: TrainBoards,
    commute: CommuteData,
    panel: WashroomPanel,
    cut: CutProgress | null,
}

// From /get-cut: derived numbers only, never the raw Health days.
interface CutProgress {
    goal: number,
    countedDays: number,
    countedThrough: string | null,
    today: { eaten: number | null, burned: number | null, weight: number | null, updated: string | null },
    source: 'notion' | 'none',
    cutKcal: number,
    totalKcal: number | null,
    startWeight: number | null,
    currentWeight: number | null,
    expectedWeight: number | null,
    expected: number | null,
    actual: number | null,
}

const TICK_MS = 1000;
const UPDATE_WEATHER_MS = 10 * 60 * 1000;
const UPDATE_EVENTS_MS = 60 * 60 * 1000;
const UPDATE_SUN_MS = 30 * 60 * 1000;
// The MTA feed itself only refreshes about every 30s.
const UPDATE_TRAINS_MS = 45 * 1000;
// Faster than the boards: this one has a deadline attached to it.
const UPDATE_COMMUTE_MS = 30 * 1000;
// Flipping the panel is a file edit on the server, not something that needs
// to land within seconds.
const UPDATE_PANEL_MS = 2 * 60 * 1000;
// Matches the server's Notion cache: a fixed calorie entry shows within ~2 min.
const UPDATE_CUT_MS = 60 * 1000;
// A Health post should show up on the meters straight away, so a tiny
// version check runs often and only a change triggers the real /get-cut.
const CHECK_HEALTH_MS = 15 * 1000;
const EVENTS_SHOWN = 3;

// Temporary layout toggles, not permanent feature removals - flip back to
// `true` to restore. Events: pulled to give the reworked subway boards room.
// Arrivals: the per-board "next train" countdown, paused for the same reason
// now that there are four boards instead of two.
const SHOW_EVENTS = false;
const SHOW_ARRIVALS = false;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

// Akatsuki cloud. Drawn as a background image rather than inline SVG: this
// tablet's WebView renders inline-SVG fills through some colour transform
// (measured: #00FF00 came out (0,71,0), #B3121D came out (255,122,102)),
// while background-image SVG - the same mechanism as the countdown wave -
// renders true. Sized in em so one class serves card and forecast alike.
function AkatsukiCloud(): JSX.Element {
    return <span className="cloud-glyph" role="img" aria-label="cloudy" />;
}

// Cloud conditions get the Akatsuki cloud; everything else keeps its emoji.
const CLOUD_CODES = [1, 2, 3];

function WeatherGlyph({ code }: { code: number | null }): JSX.Element {
    if (code !== null && CLOUD_CODES.indexOf(code) !== -1) return <AkatsukiCloud />;
    return <>{weatherIcon(code)}</>;
}

// What WeatherGlyph will actually DRAW for a code, as a comparable string. Two
// codes with the same key are the same picture, so a hop between them is not a
// visible change in the sky and isn't worth a mark on the chart.
function glyphKey(code: number | null): string {
    if (code !== null && CLOUD_CODES.indexOf(code) !== -1) return 'cloud';
    return weatherIcon(code);
}

function weatherIcon(code: number | null): string {
    if (code === null) return '🌡️';
    if (code === 0) return '☀️';
    if (code === 1 || code === 2) return '⛅';
    if (code === 3) return '☁️';
    if (code === 45 || code === 48) return '🌫️';
    if (code >= 51 && code <= 57) return '🌦️';
    if (code >= 61 && code <= 67) return '🌧️';
    if (code >= 71 && code <= 77) return '❄️';
    if (code >= 80 && code <= 82) return '🌦️';
    if (code >= 85 && code <= 86) return '🌨️';
    if (code >= 95) return '⛈️';
    return '🌡️';
}

// 14 -> "2pm", 0 -> "12am". Short enough to read across a room.
function hour12(hour: number): string {
    const suffix = hour < 12 ? 'am' : 'pm';
    const h = hour % 12 === 0 ? 12 : hour % 12;
    return `${h}${suffix}`;
}

// ------------------------------------------------------------- forecast ----
// The next twelve hours as two stacked line graphs - temperature over rain
// chance - rather than twelve separate cells. Both panels live in ONE <svg>
// rather than one each: two SVGs are each `meet`-fitted into their own box
// independently, so a one-pixel difference in either box's height changes one
// panel's scale and not the other's, and 8pm on the temperature line stops
// being above 8pm on the rain line. A single coordinate system can't disagree
// with itself.
// The viewBox aspect is matched to the box this lands in ON THE TABLET -
// roughly 348x305 - not to the one it gets in this host's preview (348x431).
// `meet` letterboxes the difference, and the device is the deliverable: the
// clock/day/date above this stack ~125px taller in the CP10's font, so a
// drawing shaped for the preview came out height-bound there and shrank to
// 250px wide, losing a quarter of its type size to side margin. Shaped for
// the device it fills the column's width; the preview just carries some slack
// under it instead.
const CH_W = 340;
const CH_H = 296;
// The left gutter carries the y labels for both panels; the right margin only
// has to keep the last point's dot off the edge.
const CH_L = 36;
const CH_R = 12;
// The temperature panel's top edge sits well below the frame because the
// condition glyphs hang above the line they mark - and the warmest hour of the
// twelve lands exactly on that edge by construction, so a glyph there needs
// somewhere to go.
const CH_TEMP_TOP = 42;
const CH_TEMP_BOT = 156;
const CH_RAIN_TOP = 198;
const CH_RAIN_BOT = 270;
const CH_HOURS_Y = 291;
// A glyph is this wide, and two of them closer together than this overlap
// into an unreadable smudge.
const CH_GLYPH = 26;

// Every coordinate ForecastChart draws against, so the same drawing serves both
// the left column's 12-hour chart and the forecast panel's 24-hour one.
interface ChartDims {
    W: number, H: number, L: number, R: number,
    TEMPTOP: number, TEMPBOT: number, RAINTOP: number, RAINBOT: number,
    HOURSY: number, GLYPH: number,
    // How many hour labels along the bottom, both ends included.
    LABELS: number,
}

const SMALL_CHART: ChartDims = {
    W: CH_W, H: CH_H, L: CH_L, R: CH_R,
    TEMPTOP: CH_TEMP_TOP, TEMPBOT: CH_TEMP_BOT, RAINTOP: CH_RAIN_TOP, RAINBOT: CH_RAIN_BOT,
    HOURSY: CH_HOURS_Y, GLYPH: CH_GLYPH, LABELS: 4,
};

// The forecast panel's chart. The viewBox is deliberately SMALLER than the
// ~870px box it lands in: every font-size in .wr-chart-* is in viewBox units,
// so drawing at ~0.64 of the box's width scales the same CSS up ~1.55x - big
// enough to read from the doorway - without a second set of type sizes.
// Aspect matched to the box left over above the week strip.
const BIG_CHART: ChartDims = {
    W: 560, H: 330, L: 40, R: 14,
    TEMPTOP: 44, TEMPBOT: 178, RAINTOP: 222, RAINBOT: 300,
    HOURSY: 322, GLYPH: 26, LABELS: 7,
};

// A label centred on the first or last sample hangs half its width off the end
// of the plot; the two extremes tuck inwards instead. Everything between is
// centred, which is what makes it read as belonging to a point.
function endAnchor(i: number, n: number): string {
    if (i === 0) return 'start';
    if (i === n - 1) return 'end';
    return 'middle';
}

function ForecastChart({ hours, dims }: { hours: HourlyPoint[], dims?: ChartDims }): JSX.Element {
    const d = dims || SMALL_CHART;
    const n = hours.length;
    const plotW = d.W - d.L - d.R;
    const x = (i: number): number => (n < 2 ? d.L + plotW / 2 : d.L + (i * plotW) / (n - 1));

    // A flat twelve hours - every value the same, which happens overnight -
    // would divide by zero here and put the line off the top of the panel. A
    // floor of four degrees on the window keeps a flat run reading as flat and
    // centred, rather than amplifying a one-degree wobble into a mountain range.
    const temps = hours.map(h => h.tempC);
    const mid = (Math.min(...temps) + Math.max(...temps)) / 2;
    const span = Math.max(Math.max(...temps) - Math.min(...temps), 4);
    const lo = Math.min(Math.min(...temps), mid - span / 2);
    const hi = Math.max(Math.max(...temps), mid + span / 2);
    const tY = (v: number): number =>
        d.TEMPBOT - ((v - lo) / (hi - lo)) * (d.TEMPBOT - d.TEMPTOP);
    const pY = (v: number): number =>
        d.RAINBOT - (v / 100) * (d.RAINBOT - d.RAINTOP);

    const tempPts = hours.map((h, i) => `${x(i)},${tY(h.tempC)}`).join(' ');
    const rainPts = hours.map((h, i) => `${x(i)},${pY(h.precip)}`).join(' ');
    // Closed back along the baseline so the area under the rain line can be
    // filled - a 0% run is then a visible flat band rather than nothing at all.
    const rainArea = `${d.L},${d.RAINBOT} ${rainPts} ${x(n - 1)},${d.RAINBOT}`;

    // One glyph for the hour we're standing in, then one wherever the sky
    // changes - which is the information the twelve-icon strip carried, minus
    // the eleven repeats of it. Compared on glyphKey rather than the raw WMO
    // code, because several codes draw the SAME picture (1, 2 and 3 are all the
    // Akatsuki cloud): "partly cloudy became overcast" is a change in the data
    // and not a change on screen, and marking it puts two identical clouds a
    // few pixels apart with a dashed line under each. A change too close to the
    // previous glyph is dropped rather than drawn overlapping.
    const marks: Array<{ i: number, code: number }> = [];
    hours.forEach((h, i) => {
        if (i > 0 && glyphKey(h.code) === glyphKey(hours[i - 1].code)) return;
        if (marks.length > 0 && x(i) - x(marks[marks.length - 1].i) < d.GLYPH) return;
        marks.push({ i, code: h.code });
    });

    // Four hour labels, evenly spread and always including both ends. A fixed
    // "every third hour" stride doesn't divide eleven, so it left the last
    // label two hours from its neighbour and the two ran into each other.
    const hourLabels: number[] = [];
    for (let j = 0; j < d.LABELS; j++) {
        const i = Math.round((j * (n - 1)) / (d.LABELS - 1));
        if (hourLabels.indexOf(i) === -1) hourLabels.push(i);
    }

    const peak = Math.max(...hours.map(h => h.precip));
    const peakI = hours.findIndex(h => h.precip === peak);

    return (
        <svg className="wr-chart" viewBox={`0 0 ${d.W} ${d.H}`}
            preserveAspectRatio="xMidYMid meet">
            {/* The guides run through BOTH panels. A change in the sky is
                usually the explanation for the step in the rain line below it,
                and a tick that stopped at the temperature axis left the reader
                eyeballing which hour it lined up with down there. */}
            {marks.map(m =>
                <line key={`g${m.i}`} className="wr-chart-guide"
                    x1={x(m.i)} y1={d.TEMPTOP - 6} x2={x(m.i)} y2={d.RAINBOT} />)}

            <text className="wr-chart-head" x={d.L} y={15}>TEMPERATURE</text>
            <line className="wr-chart-grid" x1={d.L} y1={d.TEMPTOP}
                x2={d.W - d.R} y2={d.TEMPTOP} />
            <line className="wr-chart-axis" x1={d.L} y1={d.TEMPBOT}
                x2={d.W - d.R} y2={d.TEMPBOT} />
            <text className="wr-chart-ylab" x={d.L - 7} y={d.TEMPTOP + 5}
                textAnchor="end">{Math.round(hi)}°</text>
            <text className="wr-chart-ylab" x={d.L - 7} y={d.TEMPBOT + 5}
                textAnchor="end">{Math.round(lo)}°</text>
            <polyline className="wr-chart-temp" points={tempPts} />
            {hours.map((h, i) =>
                <circle key={`t${i}`} className="wr-chart-temp-dot"
                    cx={x(i)} cy={tY(h.tempC)} r={3} />)}

            {/* foreignObject rather than an SVG <text> because a cloud is not
                an emoji here - WeatherGlyph swaps codes 1-3 for the Akatsuki
                cloud, which is a styled <span>. */}
            {marks.map(m =>
                <foreignObject key={`m${m.i}`} x={x(m.i) - d.GLYPH / 2}
                    y={Math.max(2, tY(hours[m.i].tempC) - d.GLYPH - 10)}
                    width={d.GLYPH} height={d.GLYPH}>
                    <div className="wr-chart-glyph"><WeatherGlyph code={m.code} /></div>
                </foreignObject>)}

            <text className="wr-chart-head" x={d.L} y={d.RAINTOP - 14}>RAIN CHANCE</text>
            <line className="wr-chart-grid" x1={d.L} y1={d.RAINTOP}
                x2={d.W - d.R} y2={d.RAINTOP} />
            <line className="wr-chart-grid" x1={d.L} y1={pY(50)}
                x2={d.W - d.R} y2={pY(50)} />
            <line className="wr-chart-axis" x1={d.L} y1={d.RAINBOT}
                x2={d.W - d.R} y2={d.RAINBOT} />
            <text className="wr-chart-ylab" x={d.L - 7} y={d.RAINTOP + 5}
                textAnchor="end">100</text>
            <text className="wr-chart-ylab" x={d.L - 7} y={pY(50) + 5}
                textAnchor="end">50</text>
            <text className="wr-chart-ylab" x={d.L - 7} y={d.RAINBOT + 5}
                textAnchor="end">0</text>
            <polygon className="wr-chart-rain-area" points={rainArea} />
            <polyline className="wr-chart-rain" points={rainPts} />
            {hours.map((h, i) =>
                <circle key={`p${i}`} className="wr-chart-rain-dot"
                    cx={x(i)} cy={pY(h.precip)} r={2.6} />)}
            {/* Only the wettest hour is called out by number - the rest is
                what the line is for. Suppressed at 0%, where the label would
                be a big white "0%" over a flat line that already says so. */}
            {peak > 0 &&
                <text className="wr-chart-peak" x={x(peakI)} y={pY(peak) - 9}
                    textAnchor={endAnchor(peakI, n)}>{peak}%</text>}

            {/* Four of the twelve: all twelve across this width overlap, and
                the shape of the line is the reading anyway. */}
            {hourLabels.map(i =>
                <text key={`h${i}`} className="wr-chart-hlab" x={x(i)} y={d.HOURSY}
                    textAnchor={endAnchor(i, n)}>{hour12(hours[i].hour)}</text>)}
        </svg>
    );
}

// 'YYYY-MM-DD' -> 'Today' / 'Tue'. Parsed by hand and weekday'd in UTC: a bare
// date string handed to new Date() is midnight UTC, which is the evening
// BEFORE in New York, and every label would be off by one.
function dayLabel(date: string, index: number): string {
    if (index === 0) return 'Today';
    const [y, m, d] = date.split('-').map(Number);
    return DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()].slice(0, 3);
}

// One glanceable line for what to put on before leaving. Keyed on feels-like
// and today's low rather than the current reading alone - 18° at 8am that
// drops to 9° by the walk home is a jacket day.
function wearHint(w: WashroomWeather): string | null {
    if (w.feelsLikeC === null) return null;
    const t = Math.min(w.feelsLikeC, w.lowC !== null ? w.lowC + 2 : w.feelsLikeC);
    let layer: string;
    if (t <= 0) layer = '🧣 Heavy coat';
    else if (t <= 8) layer = '🧥 Coat';
    else if (t <= 15) layer = '🧥 Jacket';
    else if (t <= 20) layer = '👕 Light layer';
    else layer = '👕 T-shirt';
    return w.rain ? `${layer} + ☂️` : layer;
}

function clockTime(d: Date): string {
    const h = d.getHours() % 12 === 0 ? 12 : d.getHours() % 12;
    return `${h}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function timeOfDay(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return clockTime(d);
}

// Compact service diagram: one strand per route down the shared trunk, then
// the fork where the branch-only routes peel off. Rendered as a single linear
// column rather than a true fork - at this size a branch would be illegible,
// and the strands simply ending says the same thing.
//
// Each board column is tall and fairly narrow, but not narrow enough for
// width to be the binding dimension - measured against the real rendered
// column (~161x354px), the column's height/width ratio (~2.2) is taller
// than the viewBox's own ratio, so `preserveAspectRatio="xMinYMin meet"` is
// WIDTH-bound: it scales up until it exactly fills the column's width,
// leaving a little unused height at the bottom, rather than unused width on
// the right. That matters beyond just filling space - the header badges
// above the spine are positioned by x-fraction of the *column's* width (see
// spineLayout below), so if the spine itself doesn't render out to the full
// column width, the badges drift right of the strands they're meant to
// label. SPINE_ROW/SPINE_MINOR_ROW/SPINE_KEY_GAP are tuned so the tallest
// board after alignment (see alignTo on ServiceSpine, below) stays safely
// under the point where it would flip into height-bound and break that
// alignment - a comfortable double-digit percent margin against the
// measured box aspect, to absorb any error in that pixel measurement.
// SPINE_LABEL_WIDTH is trimmed tighter than the longest label ("14
// St-Union Sq", ~63 units at this font size) actually needs, since a
// narrower viewBox width raises the scale factor (more px/unit) while still
// width-bound - i.e. this makes the whole diagram physically bigger, not
// just spaced out, without affecting the binding dimension.
// Raised again (14 -> 20.5 -> here) now the forecast has moved out of the
// bottom of the page and into the left column: the boards get the whole page
// height rather than ~78% of it, and a width-bound drawing only gets as tall
// as its own viewBox says, so without a matching row stride that extra height
// is just empty column. Taller rows are what cash it in - but note what they
// DON'T do. Since the binding dimension is width, rendered text px =
// fontUnits * (boxWidth / viewBoxWidth), and SPINE_ROW is in neither term. It
// buys vertical breathing room between stops, not bigger type; the viewBox
// width (SPINE_LABEL_WIDTH / SPINE_X_STEP / SPINE_LABEL_GAP) is the knob that
// scales the whole drawing. Bounded by the note above - the viewBox aspect
// has to stay under the column's, or the drawing flips to height-bound and
// the header badges stop lining up with the strands.
// Sits just under the ceiling that keeps EVERY board width-bound. A 3-route
// board's viewBox aspect is (15.550*S + 2)/144 and a 4-route board's is
// (17.745*S + 2)/165; the drawing stays width-bound while both stay under
// the column's own height/width. The columns are ~215x708 (aspect 3.29)
// after .wr-boards' gap and .wr-trains' side padding were spent on strand
// spacing, which crosses at S = 30.3. 30.0 keeps the whole set on the
// width-bound side with the drawing filling ~99% of the column's height.
// (The coefficients are measured after alignTo stretching, so they move
// slightly whenever the x-constants do - the viewBox widths they're divided
// by are also what canonCursor scales cursors by. Widening the viewBox for
// bigger chips is why the crossover moved from 29.1 to 30.3: a wider box at
// a fixed column width means each row unit renders shorter.)
const SPINE_ROW = 30.0;
// A stop with no label is just a tick on the strand - nobody's reading a
// name off it, so it only needs a fraction of a full row's height. This is
// what actually pays for SPINE_ROW being as big as it is: a line like the 1
// stops at nearly every block downtown, and the old uniform stride spent
// most of its height budget on those ticks instead of on the labels anyone
// is actually looking at.
const SPINE_MINOR_ROW = 0.45;
// Extra rows' worth of room (as a multiple of SPINE_ROW) added when a
// labelled stop directly follows another labelled stop - several sit back
// to back (Canal/WTC/Chambers on the A/C/E, 42 St/34 St) and a plain stride
// would print those names right on top of each other. This is purely about
// giving two adjacent text labels enough room not to collide - it's not a
// distance model, so it applies the same whether the two stops are one real
// stop apart (Times Sq/34 St-Herald) or separated by an unlabelled river
// crossing (Bowling Green/Borough Hall). Keep it modest for that reason: a
// bigger multiplier doesn't buy legibility past what one row's worth of text
// height needs, it just reads as a false "these are far apart".
const SPINE_KEY_GAP = 0.6;
// Left margin. Sized off the train chips rather than the strands: a
// northbound chip hangs off the LEFT of its strand, so the leftmost strand's
// widest one (SPINE_TRAIN_INSET + a two-digit chip, 15.8 units) would clip
// the viewBox edge at anything smaller. Dead space for everything else on
// the board, which is why it isn't any wider than that.
const SPINE_X_START = 15.8;
// Strand spacing. Widened along with SPINE_LINE_W - fatter lines at the old
// 12-unit stride left the gap between two strands thinner than the strands
// themselves, which reads as one wide band rather than three lines. Widened
// again for the train markers: they hang off either side of their strand
// (north left, south right) and the gap is what keeps a southbound badge on
// the 1 clear of a northbound badge on the 2. Costs ~6% of the station-name
// type size, since the drawing is width-bound - see SPINE_ROW above.
//
// Widened a third time, to 20, when the chips grew. That step was paid for
// out of the space BETWEEN the four boards rather than out of type size:
// .wr-boards' gap and .wr-trains' side padding gave each column ~10px more
// to draw in, which is what let the strand gap grow 27% (19.0px -> 24.2px)
// while the station names gave up only 5% (15.4px -> 14.6px).
//
// A fourth time, to 21, when the chips grew again. There was no space left
// between the boards to pay with, so this one comes out of type size - but
// it isn't optional: a bigger chip reaching further off its strand needs a
// wider gap to stay clear of the next strand's chip, so the step has to
// track the chip width or the collision nudging starts firing constantly.
const SPINE_X_STEP = 21;
// Right margin, between the last strand and the station names. Same sizing
// as SPINE_X_START on the other side: a southbound chip on the rightmost
// strand reaches 15.8 units past it, so anything narrower prints train
// numbers on top of station names. The first attempt used exactly that reach
// plus a rounding allowance, which is arithmetically clear and looked wrong -
// a chip landing beside a label with half a pixel between them reads as a
// collision even though it isn't one. The extra ~2.4 units are that gap.
const SPINE_LABEL_GAP = 18.2;
// Thickness of a strand, in viewBox units. Was a bare 3.6 inline; named
// because the segment rectangle now centres itself on the strand's x with it
// (it used to be drawn at x-2 for a 3.6 width, i.e. 0.2 units left of the
// dots that sit on the same line).
const SPINE_LINE_W = 4.4;
// Station-name type size, in viewBox units.
const SPINE_FONT = 9.4;
// Measured, not guessed: the longest label on any of the four boards ("14
// St-Union Sq") reports 58.3 user units via getComputedTextLength at an 8.6
// unit font, so ~63.7 at SPINE_FONT - 68 leaves ~7% for a font the tablet
// resolves slightly wider than the preview does. Keep it tight: the drawing
// is width-bound, so every unit reserved here and never used costs every
// board that fraction of its scale - i.e. it shrinks the type to reserve room
// for characters no station name on these four lines has.
const SPINE_LABEL_WIDTH = 68;

// --- Train position chips -------------------------------------------------
// One chip per train currently inside the drawn stretch, sitting where it is
// and carrying how many MINUTES separate it from the reference station (see
// placeTrain in index.js for how the server works both out). Black disc,
// white number, thin ring in the line's own colour.
//
// It counted stops first. Minutes are what anyone actually wants from a
// board read from the doorway - "eleven" answers whether to leave now, where
// "four stops" needs you to know the line - and they cost nothing extra to
// draw, since the number is the same one or two digits either way.
//
// Three earlier versions are worth not repeating. Dark fill + coloured ring
// at half this size read as damage to the diagram - twenty small holes with
// outlines round them - so it was inverted to a run-colour fill. That put
// white digits on the BMT yellow, which is illegible, and printing them in
// the line's text colour instead does not work either: this WebView's dark
// mode rewrites near-black text to white wherever it appears, in SVG <text>
// and in CSS alike. (The header's own N/Q/R/W bullets have had that bug for
// as long as they've existed - on the tablet they are white on yellow, where
// the real thing is black.) So the fill was darkened per-line by luminance,
// which worked but made the chips four slightly different colours for no
// reason a reader could name. Black for all four is the simplification: the
// one background that white is guaranteed to sit on, uniform across the
// boards, with the ring carrying the line identity instead. What made the
// first version fail was its size, not its darkness - at this size the white
// numeral is the thing you see, and the disc is just its ground.
//
// (The fill has to be a CSS background and the number a native SVG <text>:
// this WebView mangles SVG shape fill but renders text fill correctly. See
// the longer note in the stop-dot block below.)
// Grown a second time (11.5/7.6), and this one was paid for out of the
// station names rather than out of margins - there was nothing left between
// the columns to take. The whole viewBox widens with the chip, so at a fixed
// column width every other element shrinks by the same ratio: the numbers
// gain 14% (11.8px -> 13.4px) and the station names give up 4% (14.6px ->
// 14.0px). Worth it because the numbers are the part read at a distance and
// the names are read up close, but it is a real trade and there isn't much
// more of it available before the names stop being readable at all.
const SPINE_TRAIN_H = 13.5;
const SPINE_TRAIN_FONT = 9.0;
// Thin. The ring only has to say where the disc ends against a near-black
// page; any fatter and it eats the corners a two-digit number needs.
const SPINE_TRAIN_BORDER = 1.1;
// Chip width is padding plus room per digit, floored at the height so a
// one-digit chip stays a circle instead of reading as a slot. A two-digit
// one comes out 13.6 wide against a 13.5 height, i.e. still a circle to
// look at - which is what sets SPINE_X_START and SPINE_LABEL_GAP.
//
// The two-digit case is the tight one, and it's ellipse geometry, not a box
// fit: at the numeral's cap height the disc has already narrowed. Half-width
// available is (w/2)*sqrt(1 - (capHalf/(H/2))^2) - BORDER, which comes to
// ~4.87 against a 5.0 digit advance. That reads as a fifth of a unit of
// overflow and isn't one - advance width includes side bearing, so the real
// ink is narrower. The previous size sat at the same ratio and printed
// clean; anything past it starts clipping the outer digits.
const SPINE_TRAIN_PAD = 3.6;
const SPINE_TRAIN_DIGIT = 5.0;
// How far the chip's inner edge sits from its strand's centre line. Just
// inside the strand's own edge (SPINE_LINE_W / 2 = 2.2) so the chip reads as
// attached to the line rather than floating near it. Northbound chips hang
// off the left, southbound off the right - that offset is the direction
// indicator, which is why SPINE_X_STEP had to widen to fit two of them
// between adjacent strands.
const SPINE_TRAIN_INSET = 2.2;
// Minimum vertical clearance between two chips whose boxes share any x. Two
// kinds of clash need it: trains stacked on one strand heading the same way
// (relay moves at a terminal put three or four within a stop of each other,
// at literally the same y), and a southbound chip on one strand meeting a
// northbound chip on the next one along, since two chips don't quite fit in
// a SPINE_X_STEP gap. The follower is nudged down the board rather than
// hidden.
const SPINE_TRAIN_GAP = 14.4;
// ...but only so far. Past this the chip is no longer telling the truth
// about where its train is, and an honest overlap beats a confident lie, so
// the nudge gives up and lets the two chips collide.
const SPINE_TRAIN_NUDGE_MAX = 25;

// Shared between the spine SVG and the header badges above it, so the badges
// can sit at the exact same x-position as the strand they label - both are
// rendered at 100% of the same column width, so a plain x/width fraction
// lines them up regardless of the column's actual pixel size.
function spineLayout(routeCount: number): { spineX: number[], labelX: number, width: number } {
    const spineX = Array.from({ length: routeCount }, (_, r) => SPINE_X_START + r * SPINE_X_STEP);
    const labelX = spineX[spineX.length - 1] + SPINE_LABEL_GAP;
    const width = labelX + SPINE_LABEL_WIDTH;
    return { spineX, labelX, width };
}

// Official MTA line colours: IRT (1/2/3) red, IND (A/C/E) blue,
// IRT Lex (4/5/6) green, BMT Broadway (N/Q/R/W) yellow. Yellow needs dark
// text to stay legible, so text colour rides along with it.
export const LINE_COLOUR: Record<string, string> =
    { irt: '#EE352E', ind: '#002B7D', lex: '#00933C', bmt: '#FCCC0A' };
export const LINE_TEXT: Record<string, string> =
    { irt: '#fff', ind: '#fff', lex: '#fff', bmt: '#000' };

// Which board a given line's departures live on. Mirrors BOARD_BY_ROUTE in
// index.js; the commute card uses it to colour a route badge to match the
// board it's telling you to look at.
export const BOARD_BY_ROUTE: Record<string, string> = {
    1: 'irt', 2: 'irt', 3: 'irt',
    A: 'ind', C: 'ind', E: 'ind',
    4: 'lex', 5: 'lex', 6: 'lex',
    N: 'bmt', Q: 'bmt', R: 'bmt', W: 'bmt',
};

function spineColour(runColour: string): Record<string, string> {
    return {
        run: runColour,
        out: '#5a5a5f', // suspended
        add: '#FFB020', // added stop, running local
    };
}

// Unlabelled stops advance the cursor by SPINE_MINOR_ROW instead of a full
// row; labelled stops advance by a full row, plus SPINE_KEY_GAP more when
// the stop right before them was also labelled. Pulled out of ServiceSpine
// so renderBoard can call it too, to find out where a given named stop
// would naturally land before deciding whether it needs to be pulled down
// to align with another board (see alignTo below).
function computeRowCursors(spine: SpineStop[]): number[] {
    const cursors: number[] = [];
    let cursor = 6;
    spine.forEach((stop, i) => {
        if (i > 0) {
            cursor += stop.key
                ? SPINE_ROW * (spine[i - 1].key ? 1 + SPINE_KEY_GAP : 1)
                : SPINE_ROW * SPINE_MINOR_ROW;
        }
        cursors.push(cursor);
    });
    return cursors;
}

function ServiceSpine({ spine, runColour, routeCount, positions, alignTo }: {
    spine: SpineStop[], runColour: string, routeCount: number,
    // Every train the server found inside this board's drawn stretch.
    positions: TrainPosition[],
    // Optionally pulls named stops down to specific cursor values, so they
    // line up with the equivalent stops on a different board (e.g. Brooklyn
    // Bridge on the 4/5/6 with Chambers St on the A/C/E - real-world the
    // same intersection). Breakpoints apply in spine order: each one scales
    // (stretches or compresses) the rows between the previous breakpoint and
    // itself so this one lands exactly on targetCursor, so an earlier
    // alignment doesn't get undone by a later one. Compression only kicks in
    // when an earlier segment on this same board already had to stretch
    // further than this segment's own natural span needs - e.g. a board
    // that was pulled down hard to match another board's breakpoint can
    // overshoot the next target on its own natural spacing alone, and only
    // scaling that segment back down (not just carrying a constant shift
    // forward) lands it exactly on the next breakpoint too. Everything after
    // the last breakpoint carries the constant shift the last breakpoint
    // ended up needing.
    alignTo?: { name: string, targetCursor: number }[],
}): JSX.Element {
    const base = computeRowCursors(spine);
    let rowYs = base;
    if (alignTo && alignTo.length > 0) {
        rowYs = base.slice();
        const points = alignTo
            .map(a => ({ idx: spine.findIndex(s => s.name === a.name), targetCursor: a.targetCursor }))
            .filter(p => p.idx > 0)
            .sort((a, b) => a.idx - b.idx);
        let segStartIdx = 0;
        let segStartCursor = base[0];
        points.forEach(({ idx, targetCursor }) => {
            if (idx <= segStartIdx) return;
            const naturalSpan = base[idx] - base[segStartIdx];
            const desiredSpan = targetCursor - segStartCursor;
            if (naturalSpan > 0 && desiredSpan > 0) {
                const scale = desiredSpan / naturalSpan;
                for (let i = segStartIdx + 1; i <= idx; i++) {
                    rowYs[i] = segStartCursor + (base[i] - base[segStartIdx]) * scale;
                }
            } else {
                // Degenerate case: the target doesn't advance past where this
                // segment already starts (or the segment has zero natural
                // span). Scaling would collapse or invert the rows, so just
                // carry the existing shift forward instead.
                const shift = segStartCursor - base[segStartIdx];
                for (let i = segStartIdx + 1; i <= idx; i++) rowYs[i] = base[i] + shift;
            }
            segStartIdx = idx;
            segStartCursor = rowYs[idx];
        });
        const tailShift = segStartCursor - base[segStartIdx];
        for (let i = segStartIdx + 1; i < base.length; i++) rowYs[i] = base[i] + tailShift;
    }
    const height = rowYs[rowYs.length - 1] + SPINE_ROW / 2 + 2;
    const colour = spineColour(runColour);
    // A 4-route board (N/Q/R/W) needs a wider trunk than a 3-route one, so
    // the x-offsets and the label column that starts after them both scale
    // with how many strands are actually being drawn.
    const { spineX, labelX, width } = spineLayout(routeCount);

    // Fractional row index -> y. The server reports 10.4 for "40% of the way
    // from row 10 to row 11", which is exactly an interpolation of the row
    // table built above - so a train's position survives all the alignTo
    // stretching without the server needing to know any of it happened.
    const rowY = (fy: number): number => {
        const lo = Math.max(0, Math.min(rowYs.length - 1, Math.floor(fy)));
        const hi = Math.max(0, Math.min(rowYs.length - 1, lo + 1));
        return rowYs[lo] + (rowYs[hi] - rowYs[lo]) * (fy - lo);
    };
    // Chips arrive sorted by y and the nudge is always downward, so one
    // forward pass settles it: each chip is compared against everything
    // already placed and pushed under the lowest one whose box it shares any
    // x with.
    const chips: { x: number, y: number, w: number, n: number }[] = [];
    (positions || []).filter(p => spineX[p.r] !== undefined).forEach(p => {
        const w = Math.max(SPINE_TRAIN_H,
            SPINE_TRAIN_PAD + SPINE_TRAIN_DIGIT * String(p.n).length);
        // North runs toward row 0 and hangs off the left of its strand;
        // south off the right. Nothing else marks direction, so the two
        // sides have to stay consistent across all four boards.
        const dir = p.d === 'N' ? -1 : 1;
        const x = spineX[p.r] + dir * (SPINE_TRAIN_INSET + w / 2);
        const want = rowY(p.y);
        let y = want;
        chips.forEach(q => {
            if (Math.abs(q.x - x) < (q.w + w) / 2 && y < q.y + SPINE_TRAIN_GAP) {
                y = q.y + SPINE_TRAIN_GAP;
            }
        });
        if (y - want > SPINE_TRAIN_NUDGE_MAX) y = want;
        // A stack at the very bottom of the board could otherwise walk a
        // chip off the end of the viewBox, where it just gets clipped.
        y = Math.min(y, rowYs[rowYs.length - 1] + SPINE_ROW / 2 - SPINE_TRAIN_H / 2);
        chips.push({ x, y, w, n: p.n });
    });

    return (
        <div className="mta-spine">
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="xMinYMin meet">
                {spine.map((stop, i) => {
                    const y = rowYs[i];
                    // Segment bounds run to the midpoint between this row and its
                    // neighbours (half a row past the ends), not a fixed
                    // SPINE_ROW centered on y - row spacing is no longer uniform
                    // (SPINE_MINOR_ROW/SPINE_KEY_GAP weighting, alignTo
                    // stretching), so a fixed height leaves a gap wherever a
                    // row's actual neighbours are more than SPINE_ROW away.
                    // Midpoints instead guarantee each segment's edge exactly
                    // meets the next one's, whatever the spacing.
                    const topY = i === 0 ? y - SPINE_ROW / 2 : (rowYs[i - 1] + y) / 2;
                    const bottomY = i === rowYs.length - 1 ? y + SPINE_ROW / 2 : (y + rowYs[i + 1]) / 2;
                    return (
                        <g key={i}>
                            {/* Continuous strand through the row, and the stop markers below,
                                are HTML divs painted via foreignObject rather than SVG fill/
                                stroke - this WebView mangles colour inside rasterised SVG
                                (measured on this exact board: the ACE run colour, a dark navy
                                #001C53, rendered as near-white), but plain CSS background-color
                                renders pixel-exact (same fix as the .mta-bullet badges above,
                                and the cloud glyph in index.less). foreignObject content shares
                                the ambient SVG coordinate transform (viewBox scaling), so the
                                x/y/width/height geometry below is unchanged from the plain-SVG
                                version it replaces - only the colour moves from an SVG
                                attribute to a CSS one. */}
                            {stop.seg.map((st, r) => st === null ? null : (
                                <foreignObject key={`s${r}`} x={spineX[r] - SPINE_LINE_W / 2} y={topY}
                                    width={SPINE_LINE_W} height={bottomY - topY}>
                                    <div className="mta-spine-seg" style={{
                                        backgroundColor: colour[st] || colour.out,
                                        opacity: st === 'out' ? 0.5 : 1,
                                    }} />
                                </foreignObject>))}
                            {/* ...and a marker only where it actually stops. */}
                            {stop.stop.map((st, r) => {
                                if (st === null) return null;
                                const x = spineX[r];
                                const lineColour = colour[st] || colour.out;
                                // Added stops get a square, so they don't rely on colour alone.
                                if (st === 'add') {
                                    return (
                                        <foreignObject key={`m${r}`} x={x - 3.2} y={y - 3.2}
                                            width="6.4" height="6.4">
                                            <div className="mta-spine-add"
                                                style={{ backgroundColor: lineColour }} />
                                        </foreignObject>
                                    );
                                }
                                // Every stop gets a dot; the named anchors get a
                                // bigger one, so the local stops read as ticks
                                // rather than competing with the labelled ones.
                                // Sized off SPINE_LINE_W: a marker has to stay
                                // visibly bigger than the strand it sits on,
                                // so widening the line without these would
                                // have swallowed the minor ticks entirely.
                                const radius = stop.key ? 3.3 : 2.1;
                                const strokeWidth = stop.key ? 1.6 : 1.2;
                                const box = (radius + strokeWidth / 2) * 2;
                                return (
                                    <React.Fragment key={`m${r}`}>
                                        <foreignObject x={x - box / 2} y={y - box / 2}
                                            width={box} height={box}>
                                            {/* Ring only - no white background-color here. This
                                                WebView's own dark-mode treatment (Fully Kiosk on
                                                the washroom tablet, confirmed by A/B against
                                                Chrome on the same device) darkens any background/
                                                fill paint regardless of border/radius/foreignObject
                                                involvement (#ffffff -> (18,18,18) measured), and
                                                ignores the standard color-scheme/forced-color-adjust
                                                opt-outs. Literal text colour is exempt from this,
                                                same as the station-name <text> below - so the white
                                                fill is a native SVG <text> glyph, not a CSS fill. */}
                                            <div className="mta-spine-dot" style={{
                                                backgroundColor: '#1f1f22',
                                                borderColor: lineColour,
                                                borderWidth: strokeWidth,
                                            }} />
                                        </foreignObject>
                                        {st !== 'out' &&
                                            <text x={x} y={y} textAnchor="middle"
                                                dominantBaseline="central" fontSize={box * 1.71}
                                                fill="#ffffff">&#9679;</text>}
                                    </React.Fragment>
                                );
                            })}
                            {stop.key &&
                                <text x={labelX} y={y + SPINE_FONT / 2.9} fill="#ffffff" fontSize={SPINE_FONT}
                                    opacity="0.9">{stop.name}</text>}
                        </g>
                    );
                })}
                {/* Trains last, so a chip paints over the strand and stop
                    markers it sits on rather than under them. CSS background
                    + native SVG <text> for the same WebView reason as the
                    stop dots above. */}
                {chips.map((c, i) => (
                    <React.Fragment key={`t${i}`}>
                        <foreignObject x={c.x - c.w / 2} y={c.y - SPINE_TRAIN_H / 2}
                            width={c.w} height={SPINE_TRAIN_H}>
                            {/* Black disc so the white number always has the
                                same ground under it, ringed in the run
                                colour so the disc has an edge against a
                                near-black page and still says which line
                                it's on. */}
                            <div className="mta-train" style={{
                                backgroundColor: '#000000',
                                borderColor: colour.run,
                                borderWidth: SPINE_TRAIN_BORDER,
                            }} />
                        </foreignObject>
                        <text x={c.x} y={c.y + SPINE_TRAIN_FONT / 2.9} textAnchor="middle"
                            fontSize={SPINE_TRAIN_FONT} fill="#ffffff">{c.n}</text>
                    </React.Fragment>
                ))}
            </svg>
        </div>
    );
}

export default class Washroom extends React.Component<Record<string, never>, WashroomState> {
    constructor(props: Record<string, never>) {
        super(props);
        this.state = {
            now: new Date(),
            events: [],
            weather: {
                temperatureC: null, feelsLikeC: null, highC: null, lowC: null,
                precipChance: null, weatherCode: null, rain: null, hourly: [],
            },
            sun: { nextRising: null, nextSetting: null },
            trains: { irt: null, ind: null, lex: null, bmt: null },
            commute: { active: false },
            // The panel the file defaults to, so a failed config fetch still
            // lands on the same screen the server would have picked.
            panel: 'forecast',
            cut: null,
        };
        this.fetchWeather = this.fetchWeather.bind(this);
        this.fetchEvents = this.fetchEvents.bind(this);
        this.fetchSun = this.fetchSun.bind(this);
        this.fetchTrains = this.fetchTrains.bind(this);
        this.fetchCommute = this.fetchCommute.bind(this);
        this.fetchPanel = this.fetchPanel.bind(this);
        this.fetchCut = this.fetchCut.bind(this);
        this.checkHealth = this.checkHealth.bind(this);
    }

    componentDidMount(): void {
        setInterval(() => this.setState({ now: new Date() }), TICK_MS);
        this.fetchWeather();
        setInterval(this.fetchWeather, UPDATE_WEATHER_MS);
        this.fetchEvents();
        setInterval(this.fetchEvents, UPDATE_EVENTS_MS);
        this.fetchSun();
        setInterval(this.fetchSun, UPDATE_SUN_MS);
        this.fetchPanel();
        setInterval(this.fetchPanel, UPDATE_PANEL_MS);
        // Only while the boards are up - no point polling the MTA every 45s
        // for a panel nobody is looking at. fetchPanel kicks off an immediate
        // fetch when it flips to subway.
        setInterval(() => { if (this.state.panel === 'subway') this.fetchTrains(); }, UPDATE_TRAINS_MS);
        this.fetchCommute();
        setInterval(this.fetchCommute, UPDATE_COMMUTE_MS);
        this.fetchCut();
        setInterval(this.fetchCut, UPDATE_CUT_MS);
        setInterval(this.checkHealth, CHECK_HEALTH_MS);
    }

    healthVersion: number | null = null;

    checkHealth(): void {
        axios.get('/get-health-version')
            .then(r => {
                const v = r.data && r.data.v;
                if (this.healthVersion !== null && v !== this.healthVersion) this.fetchCut();
                this.healthVersion = v;
            })
            .catch(e => console.error('health version', e.toString()));
    }

    fetchCut(): void {
        axios.get('/get-cut')
            .then(r => this.setState({ cut: r.data }))
            .catch(e => console.error('cut', e.toString()));
    }

    // Two meters toward the goal weight: what the logged deficit says should
    // have come off, and what the scale's 7-day average says actually has.
    renderCut(): JSX.Element | null {
        const { cut } = this.state;
        if (!cut) return null;
        const k = (n: number) => n >= 10000 ? `${Math.round(n / 1000)}k` : n.toLocaleString('en-US');
        const meter = (cls: string, label: string, frac: number | null, value: string) => (
            <div className={`wr-cut-row ${cls}`}>
                <div className="wr-cut-label">
                    <span>{label}</span>
                    <span className="wr-cut-value">{value}</span>
                </div>
                <div className="wr-cut-track">
                    <div className="wr-cut-fill" style={{ width: `${Math.round((frac || 0) * 100)}%` }} />
                </div>
            </div>
        );
        return (
            <div className="wr-cut">
                <div className="wr-cut-title">
                    CUT TO {cut.goal} LB
                    {cut.countedThrough ? ` · THRU ${new Date(`${cut.countedThrough}T12:00:00Z`)
                        .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })
                        .toUpperCase()}` : ''}
                </div>
                {meter('wr-cut-expected', 'logged',
                    cut.expected,
                    cut.source === 'none' ? 'Notion unreachable'
                        : cut.countedDays === 0 ? 'waiting on Health'
                        : cut.totalKcal !== null
                            ? `${k(cut.cutKcal)} / ${k(cut.totalKcal)} kcal`
                            : `${k(cut.cutKcal)} kcal`)}
                {meter('wr-cut-actual', 'scale',
                    cut.actual,
                    cut.currentWeight !== null ? `${cut.currentWeight} lb` : 'no weigh-ins')}
                {this.renderCutToday(cut)}
            </div>
        );
    }

    // Today on its own: it isn't in the logged meter until it's over.
    renderCutToday(cut: CutProgress): JSX.Element {
        const { eaten, burned, weight, updated } = cut.today;
        const n = (x: number) => x.toLocaleString('en-US');
        const net = eaten !== null && burned !== null ? burned - eaten : null;
        const asOf = updated
            ? new Date(updated).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })
                .replace(/\s*([AP])M$/i, (_, p) => p.toLowerCase() + 'm')
            : null;
        return (
            <div className="wr-cut-today">
                <div className="wr-cut-label">
                    <span>today</span>
                    <span className="wr-cut-value">
                        {eaten !== null ? `${n(eaten)} in` : 'none logged'}
                        {burned !== null ? ` · ${n(burned)} out` : ''}
                        {net !== null ? ` · ${net >= 0 ? '−' : '+'}${n(Math.abs(net))}` : ''}
                    </span>
                </div>
                <div className="wr-cut-note">
                    {weight !== null ? `${weight} lb` : 'no weigh-in'}
                    {asOf ? ` · burn as of ${asOf}` : ''}
                </div>
            </div>
        );
    }

    fetchWeather(): void {
        axios.get('/get-weather')
            .then(r => this.setState({ weather: r.data }))
            .catch(e => console.error('weather', e.toString()));
    }

    fetchEvents(): void {
        axios.get('/get-events')
            .then(r => this.setState({
                events: r.data.map((e: { targetDate: string, name: string, emoji?: string }) =>
                    ({ targetDate: new Date(e.targetDate), name: e.name, emoji: e.emoji })),
            }))
            .catch(e => console.error('events', e.toString()));
    }

    fetchPanel(): void {
        axios.get('/get-washroom-config')
            .then(r => {
                const panel: WashroomPanel = r.data.panel === 'subway' ? 'subway' : 'forecast';
                if (panel === this.state.panel) return;
                this.setState({ panel });
                if (panel === 'subway') this.fetchTrains();
            })
            .catch(e => console.error('panel', e.toString()));
    }

    fetchTrains(): void {
        axios.get('/get-trains')
            .then(r => this.setState({ trains: r.data }))
            .catch(e => console.error('trains', e.toString()));
    }

    fetchCommute(): void {
        axios.get('/get-commute')
            .then(r => this.setState({ commute: r.data }))
            .catch(() => this.setState({ commute: { active: false } }));
    }

    fetchSun(): void {
        // Shares the kitchen's HA proxy; sun times are read-only sensors.
        axios.get('/get-ha')
            .then(r => { if (r.data.sun) this.setState({ sun: r.data.sun }); })
            .catch(e => console.error('ha', e.toString()));
    }

    // The commute banner. Deliberately an absolutely-positioned overlay rather
    // than another row in the flex column: the four boards' spine geometry
    // (SPINE_ROW, the alignTo breakpoints) was solved against the exact pixel
    // height these columns get, so anything that reflows them would silently
    // pull every strand off its stops. This floats over the top of them
    // instead and costs the layout nothing.
    renderCommute(): JSX.Element | null {
        const c = this.state.commute;
        if (!c.active || !c.route || !c.event || !c.trainAt) return null;

        const board = BOARD_BY_ROUTE[c.route.route] || 'irt';
        const secs = Math.max(0, Math.round((Date.parse(c.trainAt) - this.state.now.getTime()) / 1000));
        const mm = Math.floor(secs / 60);
        const ss = (secs % 60).toString().padStart(2, '0');
        const leave = c.leaveInMinutes === undefined ? 0 : c.leaveInMinutes;

        return (
            <div className="wr-commute">
                <span
                    className="wr-commute-bullet"
                    style={{ background: LINE_COLOUR[board], color: LINE_TEXT[board] }}>
                    {c.route.route}
                </span>
                <span className="wr-commute-clock">
                    {mm}<span className="wr-commute-secs">:{ss}</span>
                </span>
                <span className="wr-commute-body">
                    <span className="wr-commute-line">
                        {leave <= 0
                            ? `Go now — ${c.route.walkMinutes} min walk to ${c.route.station}`
                            : `Leave in ${leave} min — ${c.route.walkMinutes} min walk to ${c.route.station}`}
                    </span>
                    <span className="wr-commute-sub">{c.event.name}</span>
                </span>
            </div>
        );
    }

    renderBoard(board: TrainBoard | null, routes: string[], lineColour: string, textColour: string,
        key: string, alignTo?: { name: string, targetCursor: number }[],
        // The board the commute banner is pointing at. Drawn as a box-shadow
        // ring rather than a border so the column's measured size - which the
        // whole spine geometry is solved against - doesn't move a pixel.
        highlight?: boolean): JSX.Element | null {
        if (!board) return null;
        // Same x-positions the spine below draws its strands at, so each
        // badge sits directly above the line it labels.
        const { spineX, width } = spineLayout(routes.length);
        return (
            <div
                className={`wr-trains${highlight ? ' wr-trains-hot' : ''}`}
                key={key}
                style={highlight ? { boxShadow: `0 0 0 3px ${lineColour}, 0 0 26px ${lineColour}88` } : undefined}>
                <div className="wr-trains-station">{board.station}</div>
                <div className="wr-trains-head">
                    {/* Two passes: circles first, then letters on top of all
                        of them. Adjacent badges overlap at this width, and
                        with a single pass each later <span> painted over the
                        letter of the one before it - splitting the layers
                        keeps the overlapping-pill look but guarantees every
                        letter stays fully visible regardless of paint order. */}
                    {routes.map((r, i) =>
                        <span className="mta-bullet" key={r}
                            style={{
                                backgroundColor: lineColour,
                                left: `${(spineX[i] / width) * 100}%`,
                            }} />)}
                    {routes.map((r, i) =>
                        <span className="mta-bullet-label" key={r}
                            style={{
                                color: textColour,
                                left: `${(spineX[i] / width) * 100}%`,
                            }}>{r}</span>)}
                </div>

                {board.spine && board.spine.length > 0 &&
                    <ServiceSpine spine={board.spine} runColour={lineColour} routeCount={routes.length}
                        positions={board.positions} alignTo={alignTo} />}

                {SHOW_ARRIVALS && board.uptown.length + board.downtown.length > 0 &&
                    <div className="mta-board">
                        {[
                            ...board.uptown.slice(0, 1).map(t => ({ ...t, dir: '↑' })),
                            ...board.downtown.slice(0, 1).map(t => ({ ...t, dir: '↓' })),
                        ].map((t, i) =>
                            <div className="mta-row" key={i}>
                                <span className="mta-bullet small"
                                    style={{ backgroundColor: lineColour, color: textColour }}>
                                    {t.route}
                                </span>
                                <span className="mta-dest">{t.dir}</span>
                                <span className="mta-min">
                                    {t.minutes <= 0 ? 'Now' : t.minutes}
                                    {t.minutes > 0 && <em> min</em>}
                                </span>
                            </div>)}
                    </div>}
            </div>
        );
    }

    // The forecast panel: tomorrow-morning-and-beyond at a size you can read
    // from the doorway, in place of the four subway boards. The boards are
    // untouched - `{"panel": "subway"}` in washroom.json brings them back.
    renderForecastPanel(): JSX.Element {
        const { weather } = this.state;
        const hours = weather.hourlyLong || [];
        const days = weather.days || [];
        return (
            <div className="wr-bigforecast">
                {this.renderCommute()}
                {hours.length > 0 &&
                    <div className="wr-bigforecast-chart">
                        <div className="wr-forecast-title">NEXT 24 HOURS</div>
                        <ForecastChart hours={hours} dims={BIG_CHART} />
                    </div>}
                {days.length > 0 &&
                    <div className="wr-week">
                        {days.map((d, i) =>
                            <div className={`wr-day-cell${i === 0 ? ' wr-day-today' : ''}`} key={d.date}>
                                <div className="wr-day-name">{dayLabel(d.date, i)}</div>
                                <div className="wr-day-glyph"><WeatherGlyph code={d.code} /></div>
                                <div className="wr-day-hi">{d.highC}°</div>
                                <div className="wr-day-lo">{d.lowC}°</div>
                                <div className={`wr-day-rain${d.precip >= 50 ? ' wr-day-wet' : ''}`}>
                                    ☔{d.precip}%
                                </div>
                            </div>)}
                    </div>}
            </div>
        );
    }

    render(): JSX.Element {
        const { now, weather, sun, trains, panel } = this.state;
        const wear = wearHint(weather);

        const upcoming = this.state.events
            .filter(e => e.targetDate.getTime() > now.getTime())
            .sort((a, b) => a.targetDate.getTime() - b.targetDate.getTime())
            .slice(0, EVENTS_SHOWN);

        // Several real-world intersections get lined up across boards: 34
        // St, 42 St (IRT)/Grand Central (4/5/6)/Times Sq (N/Q/R/W) are the
        // 42nd St crosstown corridor, 14 St is the Union Sq corridor,
        // Chambers St (A/C)/Brooklyn Bridge-City Hall (4/5/6)/City Hall
        // (N/Q/R/W) are the same downtown intersection, and Atlantic Av
        // (IRT/4/5/6/N/Q/R/W) - which has no A/C/E equivalent, since A/C/E
        // ends at Hoyt-Schermerhorn instead - lines up with Hoyt-Schm as the
        // same kind of row (each board's final stop). The top of each board
        // needs no breakpoint: it's row 0, always the fixed start of the
        // first alignment segment, so it's already exactly aligned without
        // one (the alignTo algorithm ignores an idx-0 breakpoint outright) -
        // true whether or not row 0 happens to share a name across boards,
        // which since the 72 St cutoff it no longer always does (Lex Av has
        // no stop at 72 St, so its board starts at 68 St instead).
        //
        // Every breakpoint targets the MAX of the four boards' own natural
        // (unaligned) position there, never a single fixed reference board.
        // ServiceSpine's alignTo only ever stretches a segment to make room,
        // it never compresses one - so anchoring to anything but the max
        // would silently no-op on whichever board actually needs the most
        // room. Which board that is varies breakpoint by breakpoint (e.g.
        // the 1 stops at every local between 34 St and 14 St - 28 St, 23 St,
        // 18 St - while A/C run express there and skip straight to 23 St),
        // so no single board can be "the" reference throughout; every board,
        // A/C/E included, gets its own real alignTo now. spineX/width scale
        // with route count, so cursors get canonicalised to 3-route units
        // for the max comparison, then rescaled back to each board's own
        // width.
        const CANON_ROUTES = 3;
        const BOARDS: { key: keyof TrainBoards, routes: number, spine?: SpineStop[] }[] = [
            { key: 'irt', routes: 3, spine: trains.irt?.spine },
            { key: 'ind', routes: 3, spine: trains.ind?.spine },
            { key: 'lex', routes: 3, spine: trains.lex?.spine },
            { key: 'bmt', routes: 4, spine: trains.bmt?.spine },
        ];
        const canonCursor = (spine: SpineStop[] | undefined, name: string, routes: number) => {
            if (!spine) return null;
            const idx = spine.findIndex(s => s.name === name);
            if (idx <= 0) return null;
            return computeRowCursors(spine)[idx] * (spineLayout(CANON_ROUTES).width / spineLayout(routes).width);
        };
        // Each breakpoint's real-world equivalent name per board.
        const BREAKPOINTS: Partial<Record<keyof TrainBoards, string>>[] = [
            { irt: '34 St', ind: '34 St', lex: '33 St', bmt: '34 St-Herald' },
            { irt: '42 St', ind: '42 St', lex: 'Grand Central', bmt: 'Times Sq' },
            { irt: '14 St', ind: '14 St', lex: '14 St-Union Sq', bmt: '14 St-Union Sq' },
            { irt: 'Chambers', ind: 'Chambers', lex: 'Bklyn Bridge', bmt: 'City Hall' },
            { irt: 'Atlantic Av', ind: 'Hoyt-Schm', lex: 'Atlantic Av', bmt: 'Atlantic Av' },
        ];
        // The board the commute banner points at, ringed so it's obvious which
        // of the four to read while brushing your teeth.
        const commuteRoute = this.state.commute.active && this.state.commute.route
            ? this.state.commute.route.route : null;
        const hotBoard = commuteRoute ? BOARD_BY_ROUTE[commuteRoute] : null;

        const boardAlignTo: Record<keyof TrainBoards, { name: string, targetCursor: number }[]> =
            { irt: [], ind: [], lex: [], bmt: [] };
        BREAKPOINTS.forEach(names => {
            const canon = BOARDS.map(b => {
                const name = names[b.key];
                return name ? canonCursor(b.spine, name, b.routes) : null;
            });
            const known = canon.filter((c): c is number => c !== null);
            if (known.length === 0) return;
            const target = Math.max(...known);
            BOARDS.forEach((b, i) => {
                const name = names[b.key];
                if (!name || canon[i] === null) return;
                boardAlignTo[b.key].push({
                    name, targetCursor: target * (spineLayout(b.routes).width / spineLayout(CANON_ROUTES).width),
                });
            });
        });

        return (
            <div className="washroom">
                <div className="wr-top">
                    <div className="wr-left">
                        <div className="wr-clock-block">
                            <div className="wr-clock">
                                {clockTime(now)}
                                <span className="wr-ampm">{now.getHours() < 12 ? 'am' : 'pm'}</span>
                            </div>
                            <div className="wr-day">{DAYS[now.getDay()]}</div>
                            <div className="wr-date">{MONTHS[now.getMonth()]} {now.getDate()}</div>
                        </div>

                        <div className="wr-stats">
                            <div className="wr-stats-now">
                                <span className="wr-weather-icon">
                                    <WeatherGlyph code={weather.weatherCode} />
                                </span>
                                <span className="wr-stat-temp">
                                    {weather.temperatureC !== null ? `${weather.temperatureC}°` : '--'}
                                </span>
                                {weather.highC !== null &&
                                    <span className="wr-stat">H:{weather.highC}° L:{weather.lowC}°</span>}
                            </div>
                            {weather.feelsLikeC !== null && weather.temperatureC !== null
                                && weather.feelsLikeC !== weather.temperatureC &&
                                <div className="wr-stat">feels {weather.feelsLikeC}°</div>}
                            {/* The actionable bit: when to carry an umbrella. */}
                            <div className="wr-stat wr-rain">
                                {weather.rain
                                    ? `☔ ${hour12(weather.rain.startHour)}${weather.rain.endHour !== null ? `–${hour12(weather.rain.endHour)}` : ''} · ${weather.rain.chance}%`
                                    : '☔ none today'}
                            </div>
                            <div className="wr-stat wr-sun">
                                ☀ {timeOfDay(sun.nextRising)} · 🌙 {timeOfDay(sun.nextSetting)}
                            </div>
                        </div>

                        {/* Two line graphs filling out the rest of the left column. It
                            used to be a full-width band of twelve cells along the bottom
                            of the page, which cost the subway diagrams ~22% of their
                            height for space this column had going spare anyway. */}
                        {/* The forecast panel already draws the next 24 hours big,
                            so the column's own 12-hour chart would just be a
                            smaller copy of the first half of it. */}
                        {panel === 'forecast' && wear &&
                            <div className="wr-wear">{wear}</div>}
                        {panel === 'forecast' && this.renderCut()}
                        {panel === 'subway' && weather.hourly && weather.hourly.length > 0 &&
                            <div className="wr-forecast">
                                <div className="wr-forecast-title">NEXT 12 HOURS</div>
                                <ForecastChart hours={weather.hourly} />
                            </div>}
                    </div>

                    {panel === 'forecast' && this.renderForecastPanel()}
                    {panel === 'subway' && <div className="wr-boards">
                        {this.renderCommute()}
                        {this.renderBoard(trains.irt, ['1', '2', '3'], LINE_COLOUR.irt, LINE_TEXT.irt, 'irt',
                            boardAlignTo.irt, hotBoard === 'irt')}
                        {this.renderBoard(trains.ind, ['A', 'C', 'E'], LINE_COLOUR.ind, LINE_TEXT.ind, 'ind',
                            boardAlignTo.ind, hotBoard === 'ind')}
                        {this.renderBoard(trains.lex, ['4', '5', '6'], LINE_COLOUR.lex, LINE_TEXT.lex, 'lex',
                            boardAlignTo.lex, hotBoard === 'lex')}
                        {this.renderBoard(trains.bmt, ['N', 'Q', 'R', 'W'], LINE_COLOUR.bmt, LINE_TEXT.bmt, 'bmt',
                            boardAlignTo.bmt, hotBoard === 'bmt')}
                    </div>}
                </div>

                {SHOW_EVENTS &&
                    <div className="wr-events">
                        {upcoming.map((event, i) => {
                            const days = Math.floor(
                                (event.targetDate.getTime() - now.getTime()) / 1000 / secondsPerDay);
                            return (
                                <div className="wr-event" key={i} style={{ animationDelay: `${i * 0.09}s` }}>
                                    <span className="wr-event-days">{days}d</span>
                                    {event.emoji &&
                                        <span className="wr-event-emoji">{event.emoji}</span>}
                                    <span className="wr-event-name">{event.name}</span>
                                </div>
                            );
                        })}
                    </div>}
            </div>
        );
    }
}
